import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedService } from 'src/app/services/shared.service';
import { localStorageService } from 'src/app/services/localstorage.service';

/**
 * Which flows this business may run at a card reader.
 *
 * One switch: `doctor_profile.terminal_flow_enabled`, set by an admin as
 * `terminal_flag`. On, and the business may take a one-off payment, a
 * subscription and a financing contract at a reader; off, and none of them.
 * Pairing is hardware and this is permission, so a business that loses the
 * feature keeps its readers.
 *
 * The per-flow keys in `doctor_settings.terminal_settings` (`pp_terminal_enable`,
 * `subcription_terminal_enable`, `finance_terminal_enable`) are NOT read here any
 * more. They used to narrow the master per flow, with a different default for an
 * absent key on each, and the panel had to mirror the server's rule row for row.
 * With one switch there is nothing to mirror — but the server has to agree:
 * `purposeEnabledForBusiness` in terminalSession.service.js must stop refusing on
 * those keys too, or a button offered here is refused at `collect`.
 *
 * The flag arrives on the ordinary profile read, so there is nothing to fetch.
 */
@Injectable({ providedIn: 'root' })
export class TerminalFlagsService {

  private readonly shared = inject(SharedService);
  private readonly local = inject(localStorageService);

  private readonly liveSettings = toSignal(this.shared.doctorSettings$, { initialValue: {} as any });
  private readonly liveProfile = toSignal(this.shared.doctorProfile$, { initialValue: {} as any });

  /**
   * The profile, on a page that was opened cold.
   *
   * `doctorProfile$` is seeded with a two-field placeholder and only becomes
   * real when `access_token_login` answers — which it does *after* the router
   * has already decided what to show. Reading the placeholder would make every
   * flag here read as "absent" for the first second of a reload: the menu
   * would appear and then vanish, and a route guard asking the same question
   * would get the wrong answer at exactly the moment it is asked.
   *
   * localStorage holds the copy written by the last login, so it answers
   * straight away and is what the rest of the panel already decides on
   * (`userProfileData`, read in forty-odd components). The live value wins the
   * moment it arrives — this is a head start, not a second source of truth.
   */
  private readonly profile = computed<any>(() => {
    const live = this.liveProfile() as any;
    if (live?.doctor_id) { return live; }
    return this.local.customGetItem('userProfileData') ?? live;
  });

  private readonly settings = computed<any>(() => {
    const live = this.liveSettings() as any;
    if (live && Object.keys(live).length) { return live; }
    return this.local.customGetItem('doctorSettings') ?? live;
  });

  /**
   * The master switch: is this business on the terminal flow at all.
   *
   * Deliberately still `terminal_flow_enabled` and not `show_terminal_menu`,
   * because this one has to agree with the server: `collectOnTerminal`
   * refuses on exactly this field, so the Send to Terminal option below is
   * offered on the same basis the payment would be accepted on. The menu is a
   * wider question — see `showTerminalMenu` — and a business can legitimately
   * be refused the menu while the payment endpoint would still take a charge.
   */
  readonly terminalEnabled = computed(() => allowed((this.profile() as any)?.terminal_flow_enabled));

  /**
   * Whether the FM Terminal menu is shown at all.
   *
   * The server's whole rule in one boolean — brick-and-mortar, the flow
   * enabled, onboarding done, identity verified — worked out there so the
   * panel is not a second implementation of it. The parts ride along
   * (`blocked_on` and friends) for wording only; nothing here ANDs them.
   *
   * ## Absent hides, unlike every other flag here
   *
   * The rest of this service reads a missing value as permission, because a
   * business that predates a column should not lose a working feature to it.
   * This one is the reverse, and deliberately: the field is missing exactly
   * when the server has not deployed the rule or could not evaluate it, and
   * in both cases guessing "show" puts the menu in front of businesses the
   * rule would refuse.
   *
   * The fallback below is for the window where this panel has shipped and the
   * server has not. While `show_terminal_menu` is absent entirely we fall
   * back to the old flag, so nobody loses the menu to a deploy ordering; the
   * moment the field arrives it is obeyed exactly. Remove the fallback once
   * the server is deployed everywhere.
   */
  readonly showTerminalMenu = computed(() => {
    const flag = (this.profile() as any)?.show_terminal_menu;
    if (flag === undefined || flag === null) { return this.terminalEnabled(); }
    return flag === true || Number(flag) === 1;
  });

  /**
   * A one-off payment may be collected at a reader.
   *
   * The three flow questions below all answer to the master switch alone —
   * `terminal_flag` in the admin panel, stored as `terminal_flow_enabled`. The
   * per-flow keys in `terminal_settings` are no longer read here: one switch per
   * business, not one per flow.
   */
  readonly paymentsOnTerminal = computed(() => this.terminalEnabled());

  /**
   * The raw inputs behind `financeOnTerminal`, for a console line.
   *
   * Exists because the option is simply absent when it is off, and from the
   * screen every reason looks the same. Nothing reads this to decide anything;
   * it only reports what the decision was made from.
   */
  diagnoseFinance(): Record<string, unknown> {
    const profile = this.profile() as any;
    const raw = (this.settings() as any)?.terminal_settings;
    const parsed = this.parsed();
    return {
      profile_loaded: !!profile?.doctor_id,
      terminal_flow_enabled_raw: profile?.terminal_flow_enabled,
      terminal_settings_type: typeof raw,
      terminal_settings_raw: raw,
      blob_unparseable: parsed === null,
      finance_terminal_enable_raw: parsed === null ? '(blob unparseable)' : parsed['finance_terminal_enable'],
      master_switch: this.terminalEnabled(),
      finance_flag: this.financeOnTerminal(),
    };
  }

  /** A subscription may be enrolled at a reader. */
  readonly subscriptionsOnTerminal = computed(() => this.terminalEnabled());

  /**
   * A contract's card may be saved at a reader.
   *
   * `CONTRACT_CARD_ON_FILE` — the reader saves the card and hands back a
   * source_id; the down payment stays on `create_contract`, which raises it
   * itself. No longer default-off: it follows the master switch like the rest.
   */
  readonly financeOnTerminal = computed(() => this.terminalEnabled());

  /**
   * The blob, however it arrives — or `null` when it is there and unreadable.
   *
   * It is a TEXT column, so the profile read hands back a string; other
   * endpoints parse it first. The `null` matters: "no blob" and "a blob I
   * cannot read" are different answers now that one flow refuses on absence,
   * so they cannot both collapse to `{}` the way they used to.
   */
  private readonly parsed = computed<Record<string, unknown> | null>(() => {
    const raw = (this.settings() as any)?.terminal_settings;
    if (!raw) { return {}; }
    if (typeof raw !== 'string') { return raw as Record<string, unknown>; }
    try {
      return JSON.parse(raw) ?? {};
    } catch {
      console.warn('[terminal] terminal_settings is not valid JSON; every flow is allowed until it is fixed');
      return null;
    }
  });
}

/**
 * The master switch's own rule: absent or null means allowed, only a stored 0
 * refuses. Used for `terminal_flow_enabled` alone — the per-flow switches live
 * in a JSON blob and answer to `flow()`, which has to tell absence from
 * corruption and so cannot share this.
 */
function allowed(value: unknown): boolean {
  if (value === undefined || value === null || value === '') { return true; }
  return Number(value) === 1;
}
